'use client';

import SbsLoginSdk from '@sbs/sbs-login';
import { get } from 'lodash';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';

import { components } from '@/@types/UserActivityApi';
import LoginModal from '@/components/LoginModal';
import NavigationMenu from '@/components/NavigationMenu';
import type { UserData } from '@/components/RewindSlides';
import getSbsLoginInstance from '@/components/SbsLoginInstance';
import TestConsole from '@/components/TestConsole';
import useIsMounted from '@/hooks/useIsMounted';
import RevealJsProvider from '@/providers/RevealJsProvider';
import {
  fetchAllFavourites,
} from '@/services/FavouritesApi';
import { fetchData as fetchRecommendationData, RecommendationData } from '@/services/RecommendationApi';
import { fetchData as fetchRewindData, RewindApiError, RewindData } from '@/services/RewindApi';
import DataLayer from '@/utils/DataLayer';

// Client side loading of components. These require the "window" object that does not exist on the server side.
const RewindSlides = dynamic(() => { return import('@/components/RewindSlides'); }, { ssr: false });
const NrBrowserAgent = dynamic(() => { return import('@/utils/nr-browser-agent'); }, { ssr: false });

const Home = () => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [ready, setReady] = useState<boolean>(false);
  const [sbsLogin, setSbsLogin] = useState<SbsLoginSdk>();
  const [userData, setUserData] = useState<UserData | undefined>(undefined);
  const [rewindData, setRewindData] = useState<RewindData | RewindApiError | null | undefined>(undefined);
  const [recommendationData, setRecommendationData] = useState<RecommendationData | null | undefined>(undefined);
  const [
    favouritesData,
    setFavouritesData,
  ] = useState<components['schemas']['models.FavouriteResponse'][]>([]);
  const [showTestConsole, setShowTestConsole] = useState(false);
  const [rotateDeviceMessage, setRotateDeviceMessage] = useState('');
  const isMounted = useIsMounted();

  useEffect(() => {
    if (isMobile) {
      setRotateDeviceMessage('To enhance your SBS Rewind experience, please rotate your device.');
    } else {
      setRotateDeviceMessage('To enhance your SBS Rewind experience, please expand your browser window.');
    }

    DataLayer.init();
  }, []);

  useEffect(() => {
    const handleKeypress = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.code === 'KeyT') {
        setShowTestConsole(!showTestConsole);
      }
    };

    document.addEventListener('keypress', handleKeypress);

    return () => {
      document.removeEventListener('keypress', handleKeypress);
    };
  }, [showTestConsole]);

  useEffect(() => {
    const onReady = async () => {
      if (sbsLogin) {
        setIsLoggedIn(await sbsLogin.isLoggedIn());
        setReady(true);

        if (isLoggedIn) {
          sbsLogin.getEmailHash().then(async (emailHash) => {
            DataLayer.updateUserData({
              userId: await sbsLogin.getUserId(),
              emailHash: emailHash || '',
            });
            DataLayer.events.pageLoad();
            DataLayer.events.screenChange('welcome');
          });
        } else {
          DataLayer.events.pageLoad();
          DataLayer.events.screenChange('welcome');
        }
      }
    };

    const onSignInSuccess = async () => {
      if (sbsLogin) {
        setIsLoggedIn(await sbsLogin.isLoggedIn());
        sbsLogin.close();
        sbsLogin.getEmailHash().then(async (emailHash) => {
          DataLayer.updateUserData({
            userId: await sbsLogin.getUserId(),
            emailHash: emailHash || '',
          });
        });
      }
    };

    const onSignOutSuccess = async () => {
      if (sbsLogin) {
        setIsLoggedIn(await sbsLogin.isLoggedIn());
        const event = new Event('OdWebsite_SignOutSuccess');
        document.dispatchEvent(event);
        setUserData(undefined);
        setRewindData(undefined);
      }
    };

    if (!sbsLogin) {
      setSbsLogin(getSbsLoginInstance());
    } else if (sbsLogin) {
      sbsLogin.on('ready', onReady);
      sbsLogin.on('signInSuccess', onSignInSuccess);
      sbsLogin.on('signOutSuccess', onSignOutSuccess);
    }

    return () => {
      if (sbsLogin) {
        sbsLogin.off('ready', onReady);
        sbsLogin.off('signInSuccess', onSignInSuccess);
        sbsLogin.off('signOutSuccess', onSignOutSuccess);
      }
    };
  }, [isLoggedIn, isMounted, sbsLogin]);

  useEffect(() => {
    if (sbsLogin && isLoggedIn) {
      sbsLogin.isLoggedIn().then(async () => {
        setUserData({
          firstname: '',
          userId: process.env.NEXT_PUBLIC_TEST_USERID || await sbsLogin.getUserId(),
        });
      });

      sbsLogin.getAccessToken().then(async (accessToken) => {
        fetchRewindData(accessToken).then(async (data) => {
          setUserData({
            firstname: get(data, 'user.firstname', ''),
            userId: process.env.NEXT_PUBLIC_TEST_USERID || await sbsLogin.getUserId(),
          });
          setRewindData(data);
          fetchRecommendationData(
            await sbsLogin.getUserId(),
            accessToken,
          ).then((recData) => {
            setRecommendationData(recData);
          });
        });

        fetchAllFavourites(accessToken).then((data) => {
          setFavouritesData(data);
        });
      });
    } else {
      setUserData(undefined);
    }
  }, [sbsLogin, isLoggedIn]);

  const handleTestDataChange = (json: string) => {
    const data = JSON.parse(json);
    setUserData(data.userData);
    setRewindData(data.rewindData);
    setRecommendationData(data.recommendationData);
  };

  return (
    <RevealJsProvider>
      <NrBrowserAgent/>
      <NavigationMenu
        userData={userData}
        ready={ready}
        isLoggedIn={isLoggedIn}
      />
      <div className="group-[&.ready]/body:hidden w-full h-full flex flex-col justify-center items-center opacity-0 animate-fadein animate-delay-[5000ms] animate-fill-forwards">
        <div className="border-dark-tangerine h-20 w-20 animate-spin rounded-full border-8 border-t-black"/>
      </div>
      <RewindSlides
        userData={userData}
        rewindData={rewindData}
        recommendationData={recommendationData}
        isLoggedIn={!!isLoggedIn}
        favouritesData={favouritesData}
      />
      {process.env.NEXT_PUBLIC_ENABLE_TEST_CONSOLE && showTestConsole && userData && (
        <TestConsole
          data={
            JSON.stringify({
              userData,
              rewindData,
              recommendationData,
            }, null, 4)
          }
          onChange={handleTestDataChange}
        />
      )}
      <div
        className="portrait-only hidden text-2xl h-full w-full absolute z-10 flex-col items-center justify-center gap-8 px-2 text-center"
      >
        <div className="w-[80%] text-onyx">{rotateDeviceMessage}</div>
        <a
          type="button"
          className="sbs-button sbs-button-primary sbs-button-small button w-[220px] h-[40px] no-underline"
          href="https://www.sbs.com.au/ondemand"
        >
          Visit homepage
        </a>
      </div>
      <LoginModal sbsLogin={sbsLogin}/>
    </RevealJsProvider>
  );
};

export default Home;
